// ===== BEGIN FILE: src/components/report-v2/reportDataBuilder.ts =====
/**
 * Utility to convert App.tsx data into ReportData format
 */

import type { ReportData } from './types';
import { computeDashaImpactByHouse, computeTransitImpactByHouse } from '../../utils/houseTemporalScores';
import type { LordDusthanaPenalty } from '../../utils/lordshipUtils';
// NOTE: We do NOT use getPlanetDignity() (which has ±10° degree tolerance) —
// instead we use sign-based dignity matching App.tsx getDignity() for consistency.

// Sign-based dignity check (0-indexed signs: Aries=0, Taurus=1, ... Pisces=11)
// Matches App.tsx EXALT_SIGN/getDignity — sign-based (whole-sign exaltation)
const EXALT_SIGN_0: Record<string, number> = { Sun:0, Moon:1, Mars:9, Mercury:5, Jupiter:3, Venus:11, Saturn:6 };
const DEBIL_SIGN_0: Record<string, number> = { Sun:6, Moon:7, Mars:3, Mercury:11, Jupiter:9, Venus:5, Saturn:0 };
const OWN_SIGN_0:   Record<string, number[]> = { Sun:[4], Moon:[3], Mars:[0,7], Mercury:[2,5], Jupiter:[8,11], Venus:[1,6], Saturn:[9,10] };
const MOOLATRIKONA_0: Record<string, [number, number, number]> = {
  Sun:[4,0,20], Moon:[1,3,30], Mars:[0,0,12], Mercury:[5,15,20],
  Jupiter:[8,0,10], Venus:[6,0,15], Saturn:[9,0,20]
};
// Sign lords (0-indexed) for Friend/Enemy/Neutral calculation
const SIGN_LORD_0: Record<number, string> = {
  0:'Mars',1:'Venus',2:'Mercury',3:'Moon',4:'Sun',5:'Mercury',
  6:'Venus',7:'Mars',8:'Jupiter',9:'Saturn',10:'Saturn',11:'Jupiter'
};
const NAT_FRIENDS: Record<string, string[]> = {
  Sun:['Moon','Mars','Jupiter'], Moon:['Sun','Mercury'],
  Mars:['Sun','Moon','Jupiter'], Mercury:['Sun','Venus'],
  Jupiter:['Sun','Moon','Mars'], Venus:['Mercury','Saturn'],
  Saturn:['Mercury','Venus'], Rahu:['Saturn','Venus'], Ketu:['Mars','Jupiter'],
};
const NAT_ENEMIES: Record<string, string[]> = {
  Sun:['Venus','Saturn'], Moon:[], Mars:['Mercury'], Mercury:['Moon'],
  Jupiter:['Mercury','Venus'], Venus:['Sun','Moon'],
  Saturn:['Sun','Moon','Mars'], Rahu:['Sun','Moon'], Ketu:['Moon','Venus'],
};

function getSignBasedDignity(name: string, lon: number): string {
  if (name === 'Rahu' || name === 'Ketu') return 'Neutral';
  const signIdx = Math.floor(((lon % 360) + 360) % 360 / 30);
  const degInSign = ((lon % 360) + 360) % 360 % 30;
  if (EXALT_SIGN_0[name] === signIdx) return 'Exalted';
  if (DEBIL_SIGN_0[name] === signIdx) return 'Debilitated';
  const mt = MOOLATRIKONA_0[name];
  if (mt && signIdx === mt[0] && degInSign >= mt[1] && degInSign <= mt[2]) return 'Moolatrikona';
  if (OWN_SIGN_0[name]?.includes(signIdx)) return 'Own Sign';
  const lord = SIGN_LORD_0[signIdx];
  if ((NAT_FRIENDS[name] || []).includes(lord)) return 'Friend';
  if ((NAT_ENEMIES[name] || []).includes(lord)) return 'Enemy';
  return 'Neutral';
}

export function buildReportData(params: {
  astroData: any;
  houseScores: Record<number, number> | null;
  bhavaBala?: Record<number, number> | null;
  birthDetails?: {
    name?: string;
    dateOfBirth?: string;
    timeOfBirth?: string;
    location?: string;
    coordinates?: { lat: number; lon: number };
    timezone?: string;
    ascendantPersonality?: string;
    moonNakshatraDesc?: string;
    houseSignNotes?: Record<number, string>;
  };
  chartImageUrl?: string;
  yogas?: Array<any>;
  dashaContext?: any;
  sadeSati?: { isActive: boolean; currentPhase?: string; from?: string; to?: string } | null;
  blendedPlanetPct?: Partial<Record<string, number>>;
  functionalMap?: Record<string, string>;
  houseInterpretations?: Array<any>;
  lifeAreaRemedies?: Record<string, Array<{ title: string; description?: string; planet: string; isImportant?: boolean }>>;
  transitPositions?: Array<{ planet: string; house: number }>;
  feeds?: Array<{
    id: string;
    lifeArea: string;
    severity: 'positive' | 'mixed' | 'caution' | 'alert';
    score: number;
    message: string;
    window: { from: string; to: string };
    evidence: string[];
    ruleKey?: string;
    private?: boolean;
  }>;
  /** House activities from Supabase houses.activities column (keyed by house 1-12) */
  houseActivities?: Record<number, string>;
  /** Shadbala % per planet — from computeShadbalaPercentages() */
  shadbalaPct?: Record<string, { totalPct: number; components: { sthana: number; dig: number; kala: number; cheshta: number; naisargika: number; drik: number } }>;
  /** Shadbala virupa ratios — from estimateShadbalaRatios() (7 classical planets only) */
  shadbalaRatio?: Record<string, { virupa: number; minRequired: number; ratio: number }>;
  /** Ishta-Kashta Phala — from computeIshtaKashtaPhala() */
  ikPhala?: Record<string, { ishta: number; kashta: number; ishtaPct: number; kashtaPct: number }>;
  /** UTC birth datetime ISO string — used for year-ahead dasha forecast */
  utcBirthISO?: string;
  /** Lord dusthana penalties — computed from ascendant + planet placements in App.tsx */
  lordDusthanaPenalties?: Record<number, LordDusthanaPenalty>;
}): ReportData | null {
  const { astroData, houseScores, bhavaBala, birthDetails, chartImageUrl, yogas = [], dashaContext, sadeSati, blendedPlanetPct, functionalMap, houseInterpretations: houseInterps, lifeAreaRemedies, transitPositions, feeds, houseActivities, shadbalaPct, shadbalaRatio, ikPhala, utcBirthISO, lordDusthanaPenalties } = params;

  // Return null if essential data is missing
  if (!astroData || !houseScores) {
    return null;
  }

  // Get ascendant sign name
  const signNames = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  
  // AstroData has asc.ascendantSignIndex (1-12)
  const ascendantSignIndex = astroData.asc?.ascendantSignIndex || 1;
  const ascendantSign = signNames[ascendantSignIndex - 1] || 'Unknown';

  // Get ascendant lord
  const signLords: Record<number, string> = {
    1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
    7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
  };
  const ascendantLord = signLords[ascendantSignIndex] || 'Unknown';

  // Get moon sign from grahas.Moon
  const moonLon = astroData.grahas?.Moon?.lon || 0;
  const moonSignIndex = Math.floor(moonLon / 30) + 1; // 0-11 -> 1-12
  const moonSign = signNames[moonSignIndex - 1] || 'Unknown';

  // Get moon nakshatra from moon data
  const nakshatraNames = [
    'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
    'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
    'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
    'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
    'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
  ];
  // Fixed: getMoonNakshatraAndRashi returns .nakshatra (string) and .nakIndex (1-based), not .nakshatraIndex/.pada
  const moonSidLon = (astroData.moon?.siderealLongitudeDeg ?? moonLon) as number;
  const _nakWidth = 360 / 27;
  const moonPada = Math.floor((moonSidLon % _nakWidth) / (_nakWidth / 4)) + 1;
  const moonNakstrName = (astroData.moon?.nakshatra as string)
    || nakshatraNames[(astroData.moon?.nakIndex ?? 1) - 1]
    || 'Unknown';
  const moonNakshatra = `${moonNakstrName} - Pada ${moonPada}`;

  // Ascendant nakshatra
  const ascendantNakshatra = (astroData.asc?.ascendantNakshatra as string) || 'Unknown';

  // House cusps (whole-sign system) — cusps[i] is the start-of-sign longitude
  // For whole-sign: H1 cusp = actual Ascendant degree within sign; H2-H12 cusp = 0°00' of the sign
  const _ascSidLon = Number(astroData.asc?.siderealLongitudeDeg) || 0;
  // Format cusp degree within its sign.
  // H1: uses the actual ascendant sidereal longitude → gives exact degree within Cancer/Aries/etc.
  // H2-H12: whole-sign boundaries are multiples of 30°, so in-sign degree is always 0°00′00″.
  const _formatCusp = (_lon: number, isAscendant: boolean): string => {
    const sourceLon = isAscendant ? _ascSidLon : _lon;
    const norm = ((sourceLon % 360) + 360) % 360;
    const signStart = Math.floor(norm / 30) * 30;
    const inSign = norm - signStart;
    let d = Math.floor(inSign);
    let mFloat = (inSign - d) * 60;
    let m = Math.floor(mFloat);
    let s = Math.round((mFloat - m) * 60);
    if (s === 60) { s = 0; m += 1; }
    if (m === 60) { m = 0; d += 1; }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d}\u00B0${pad(m)}\u2032${pad(s)}\u2033`;
  };
  // ── Dasha & Transit Impact per house — delegated to shared utility ──────────
  const _dashaImpactByHouse = computeDashaImpactByHouse(dashaContext, functionalMap);
  const _transitImpactByHouse = computeTransitImpactByHouse(transitPositions, functionalMap, blendedPlanetPct);
  // ISO timestamp for when temporal metrics were computed (used for the report footnote)
  const _houseMetricsTimestamp = new Date().toISOString();

  const houseCusps = ((astroData.cusps as number[]) || []).map((lon: number, idx: number) => {
    const sIdx = Math.floor(((lon % 360) + 360) % 360 / 30);
    const isH1 = idx === 0;
    const hNum = idx + 1;
    return {
      house: hNum,
      sign: signNames[sIdx] || 'Unknown',
      cusp: _formatCusp(lon, isH1),
      bhavaPct: bhavaBala ? Math.round(bhavaBala[hNum] ?? 0) : undefined,
      dashaImpact: _dashaImpactByHouse[hNum],
      transitImpact: _transitImpactByHouse[hNum],
      isWholeSign: !isH1,
      signNote: birthDetails?.houseSignNotes?.[hNum],
    };
  });

  // Planets in houses — grouped by house number
  const _houseGroups: Record<number, string[]> = {};
  for (let h = 1; h <= 12; h++) _houseGroups[h] = [];
  const _planetOrder = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  _planetOrder.forEach(p => {
    const house = (astroData.placements as any)?.[p]?.house as number | undefined;
    if (house && house >= 1 && house <= 12) _houseGroups[house].push(p);
  });
  const planetsInHouses = houseCusps.map(({ house, sign }) => {
    const houseInterp = houseInterps?.find((hi: any) => hi.house === house);
    // Lord-in-house: use first lordship entry for this house (primary lord)
    const lih = houseInterp?.lordship?.[0];
    return {
      house,
      sign,
      lihPosition: lih ? lih.position.replace(' lord in ', ` Lord ${lih.lord} in `) : undefined,
      lihNote: (lih?.interpretation || lih?.overview) as string | undefined,
      planets: (_houseGroups[house] || []).map((pName: string) => {
        const nature = functionalMap?.[pName] || 'neutral';
        const funcNature = (nature === 'benefic' || nature === 'malefic' || nature === 'yoga_karaka') ? nature : 'neutral';
        const pihPlanet = houseInterp?.planets?.find((p: any) => p.planet === pName);
        const pihNote = pihPlanet?.interpretation || pihPlanet?.housePlanetNote;
        return {
          name: pName,
          functionalNature: funcNature as 'benefic' | 'malefic' | 'neutral' | 'yoga_karaka',
          blendedPct: blendedPlanetPct?.[pName],
          pihNote,
        };
      }),
    };
  });

  // Planetary aspects — Parasara house-based (computed directly from house positions)
  // Using house positions avoids duplicates that arise when multiple planets occupy the same aspected house.
  const planetaryAspects: Array<{ from: string; fromHouse: number; toHouse: number; kind: string }> = [];
  const _addH = (h: number, offset: number) => ((h - 1 + offset) % 12) + 1;
  const _aspectPlanets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  for (const pName of _aspectPlanets) {
    const fromHouse = astroData.placements?.[pName]?.house as number | undefined;
    if (!fromHouse) continue;
    // 7th aspect — everyone
    planetaryAspects.push({ from: pName, fromHouse, toHouse: _addH(fromHouse, 6), kind: '7th aspect' });
    // Special aspects — matches parashariAspects.ts DRISHTI_HOUSE_DIFF
    if (pName === 'Mars') {
      planetaryAspects.push({ from: pName, fromHouse, toHouse: _addH(fromHouse, 3), kind: '4th aspect' });
      planetaryAspects.push({ from: pName, fromHouse, toHouse: _addH(fromHouse, 7), kind: '8th aspect' });
    } else if (pName === 'Jupiter' || pName === 'Rahu' || pName === 'Ketu') {
      // Rahu & Ketu share Jupiter's 5th/9th aspects (canonical Parasara rule)
      planetaryAspects.push({ from: pName, fromHouse, toHouse: _addH(fromHouse, 4), kind: '5th aspect' });
      planetaryAspects.push({ from: pName, fromHouse, toHouse: _addH(fromHouse, 8), kind: '9th aspect' });
    } else if (pName === 'Saturn') {
      planetaryAspects.push({ from: pName, fromHouse, toHouse: _addH(fromHouse, 2), kind: '3rd aspect' });
      planetaryAspects.push({ from: pName, fromHouse, toHouse: _addH(fromHouse, 9), kind: '10th aspect' });
    }
  }

  // Build planets array from grahas and nodes
  const planetNames = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const planets = planetNames.map(name => {
    const graha = astroData.grahas[name];
    if (!graha) return null;
    
    const lon = graha.lon;
    const signIdx = Math.floor(lon / 30);
    const degInSign = lon % 30;
    const nakIdx = Math.floor(lon / (360 / 27));
    const pada = Math.floor((lon % (360/27)) / (360/27/4)) + 1;
    const house = astroData.placements?.[name]?.house || 1;
    
    // Sign-based dignity — matches App.tsx getDignity (whole-sign exaltation, no degree tolerance)
    const dignityLabel = getSignBasedDignity(name, lon);
    
    return {
      name: name as any,
      sign: signNames[signIdx] || 'Unknown',
      house,
      degree: Math.round(degInSign * 100) / 100,
      nakshatra: nakshatraNames[nakIdx] || 'Unknown',
      pada,
      lord: signLords[signIdx + 1] || 'Unknown',
      dignity: dignityLabel,
      isRetrograde: graha.retrograde || false,
      isCombust: graha.combust || false,
      isExalted: EXALT_SIGN_0[name] === Math.floor(((lon % 360) + 360) % 360 / 30),
      isDebilitated: DEBIL_SIGN_0[name] === Math.floor(((lon % 360) + 360) % 360 / 30),
    };
  }).filter(Boolean);

  // Add Rahu and Ketu
  if (astroData.nodes?.Rahu) {
    const rahuLon = astroData.nodes.Rahu;
    const signIdx = Math.floor(rahuLon / 30);
    const degInSign = rahuLon % 30;
    const nakIdx = Math.floor(rahuLon / (360 / 27));
    const pada = Math.floor((rahuLon % (360/27)) / (360/27/4)) + 1;
    const house = astroData.placements?.Rahu?.house || 1;
    
    planets.push({
      name: 'Rahu' as any,
      sign: signNames[signIdx] || 'Unknown',
      house,
      degree: Math.round(degInSign * 100) / 100,
      nakshatra: nakshatraNames[nakIdx] || 'Unknown',
      pada,
      lord: signLords[signIdx + 1] || 'Unknown',
      dignity: 'Neutral',
      isRetrograde: false,
      isCombust: false,
      isExalted: false,
      isDebilitated: false,
    });
  }

  if (astroData.nodes?.Ketu) {
    const ketuLon = astroData.nodes.Ketu;
    const signIdx = Math.floor(ketuLon / 30);
    const degInSign = ketuLon % 30;
    const nakIdx = Math.floor(ketuLon / (360 / 27));
    const pada = Math.floor((ketuLon % (360/27)) / (360/27/4)) + 1;
    const house = astroData.placements?.Ketu?.house || 1;
    
    planets.push({
      name: 'Ketu' as any,
      sign: signNames[signIdx] || 'Unknown',
      house,
      degree: Math.round(degInSign * 100) / 100,
      nakshatra: nakshatraNames[nakIdx] || 'Unknown',
      pada,
      lord: signLords[signIdx + 1] || 'Unknown',
      dignity: 'Neutral',
      isRetrograde: false,
      isCombust: false,
      isExalted: false,
      isDebilitated: false,
    });
  }

  // Build life areas from house scores (matching LifeAreasDashboard structure)
  const lifeAreaDefinitions = [
    {
      area: 'Health',
      houses: [1, 6, 8, 12],
      description: 'Physical vitality, diseases, chronic issues, hospitalization'
    },
    {
      area: 'Wealth',
      houses: [1, 2, 5, 9, 11, 12],
      description: 'Income, assets, speculations, fortune, gains, expenses'
    },
    {
      area: 'Job',
      houses: [1, 6, 10],
      description: 'Career, service, professional reputation'
    },
    {
      area: 'Business',
      houses: [1, 3, 7, 11],
      description: 'Entrepreneurship, partnerships, gains from ventures'
    },
    {
      area: 'Marriage',
      houses: [1, 7],
      description: 'Spouse, partnerships, marital harmony'
    },
    {
      area: 'Children',
      houses: [1, 5],
      description: 'Children, creativity, romance'
    }
  ];

  const getRating = (score: number) => {
    if (score >= 75) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 30) return 'Fair';
    return 'Needs Attention';
  };

  const lifeAreas = lifeAreaDefinitions.map(({ area, houses, description }) => {
    // Permanent metric 1: Planet Impact Score (raw average, -50 to +50)
    const planetImpactScore = houses.reduce((sum, h) => sum + (houseScores[h] ?? 0), 0) / houses.length;

    // Permanent metric 2: Bhava Bala (0-100) — average across relevant houses
    let bhavaScore: number | undefined = undefined;
    if (bhavaBala) {
      const bhavaValues = houses.map(h => bhavaBala[h] ?? 0);
      bhavaScore = bhavaValues.reduce((sum, val) => sum + val, 0) / houses.length;
    }

    // Temporal metric 1: Dasha Impact (0-100) — average activation across houses
    const dashaImpactScore = houses.reduce((s, h) => s + (_dashaImpactByHouse[h] ?? 50), 0) / houses.length;

    // Temporal metric 2: Transit Impact (0-100) — average transit influence
    const transitImpactScore = houses.reduce((s, h) => s + (_transitImpactByHouse[h] ?? 50), 0) / houses.length;

    // Weighted combined (65% natal, 35% temporal):
    // Natal component = average of bhavaScore (or normalized planetImpact) + normalized planetImpact
    // Temporal component = average of dashaImpact + transitImpact
    const normalizedPlanetImpact = ((planetImpactScore + 50) / 100) * 100; // map -50..+50 → 0-100
    const natalComponent = bhavaScore !== undefined
      ? (bhavaScore + normalizedPlanetImpact) / 2
      : normalizedPlanetImpact;
    const temporalComponent = (dashaImpactScore + transitImpactScore) / 2;
    const combinedScore = Math.round(natalComponent * 0.65 + temporalComponent * 0.35);

    // Legacy displayScore: keep using natal-only for backward compat with existing sort/rating logic
    const displayScore = combinedScore;

    return {
      area: area as any,
      score: displayScore,
      rating: getRating(displayScore),
      houses: houses.sort((a, b) => a - b),
      description,
      planetImpactScore,
      bhavaScore,
      dashaImpactScore: Math.round(dashaImpactScore),
      transitImpactScore: Math.round(transitImpactScore),
      combinedScore,
      remedies: lifeAreaRemedies?.[area] ?? undefined,
    };
  });

  // Process yogas
  const rawYogas = yogas.map((yoga: any) => ({
    name: yoga.name || yoga.message?.split(':')[0] || 'Unknown Yoga',
    type: yoga.type || yoga.ruleKey || 'General',
    severity: yoga.severity || 'mixed',
    description: yoga.message || yoga.description || 'No description available',
    isPermanent: yoga.isPermanent !== undefined ? yoga.isPermanent : true,
    isActive: yoga.isActive !== undefined ? yoga.isActive : false,
    private: yoga.private || false,
  }));

  // Add Sade Sati if active
  if (sadeSati?.isActive) {
    const phaseLabel = sadeSati.currentPhase === 'rising' ? 'Rising Phase (Saturn in 12th from Moon)'
      : sadeSati.currentPhase === 'peak' ? 'Peak Phase (Saturn in Moon sign)'
      : sadeSati.currentPhase === 'setting' ? 'Setting Phase (Saturn in 2nd from Moon)'
      : 'Active';
    rawYogas.push({
      name: 'Sade Sati',
      type: 'sade_sati',
      severity: 'caution',
      description: `Sade Sati (7.5-year Saturn cycle) is currently active — ${phaseLabel}. This is a period of deep introspection, karmic lessons, and transformation. Period: ${sadeSati.from || 'N/A'} to ${sadeSati.to || 'N/A'}.`,
      isPermanent: false,
      isActive: true,
      private: false,
    });
  }

  // Deduplicate yogas: normalize ruleKey (_natal/_timing suffix stripped) as primary key, name as fallback
  const seenYogaKeys = new Set<string>();
  const processedYogas = rawYogas.filter(y => {
    const normalizedType = (y.type || '').toLowerCase().replace(/_natal$/, '').replace(/_timing$/, '').trim();
    const nameKey = y.name.toLowerCase().trim();
    // Use normalizedType if meaningful, otherwise fall back to name
    const key = normalizedType && normalizedType !== 'general' ? normalizedType : nameKey;
    if (seenYogaKeys.has(key)) return false;
    seenYogaKeys.add(key);
    // Also register the name key to catch name-based dups
    if (key !== nameKey) seenYogaKeys.add(nameKey);
    return true;
  });

  // Build dasha data
  // House names for spotlight descriptions (matches FeedsList.tsx)
  const DASHA_HOUSE_NAMES = [
    'Self and Personality', 'Money, Family and Possessions', 'Siblings and Communication',
    'Mother and Home', 'Children, Creativity and Romance', 'Job, Health and Service',
    'Marriage and Partnerships', 'Transformation, Mysteries and Hidden Knowledge',
    'Fortune, Luck and Father', 'Career and Status', 'Gains, Income and Friends',
    'Troubles, Losses and Spiritual Awakening',
  ];
  const DASHA_HOUSE_TO_LIFE: Record<number, string[]> = {
    1: ['Self','Health','AccidentRisk','Travel'], 2: ['Wealth'],
    3: ['Travel'], 4: ['Mother','Property'], 5: ['Children','Investments'],
    6: ['Health','Litigation'], 7: ['Spouse','Travel'],
    8: ['AccidentRisk','Health','Investments','Spirituality'],
    9: ['Father','Spirituality','Travel'], 10: ['Career','Property'],
    11: ['Wealth','Investments','Career'], 12: ['Spirituality','Travel','Health'],
  };

  const getDashaExtras = (level: any) => {
    if (!level) return {};
    // Use Moon longitude for Rahu/Ketu (matches FeedsList.tsx logic)
    const usePlanet = (level.planet === 'Rahu' || level.planet === 'Ketu') ? 'Moon' : level.planet;
    let lon = 0;
    if (!((astroData as any)?.grahas?.[usePlanet])) {
      lon = (astroData as any)?.nodes?.[usePlanet] ?? 0;
    } else {
      lon = (astroData as any).grahas[usePlanet]?.lon ?? 0;
    }

    const nakIndex0 = Math.floor(((lon % 360) + 360) % 360 / (360 / 27));
    const rashiIndex0 = Math.floor(((lon % 360) + 360) % 360 / 30);
    const nakshatra = nakshatraNames[nakIndex0] || '';
    const zodiacSign = signNames[rashiIndex0] || '';

    const occupiesHouse: number = level.occupies ?? 1;
    const spotlight = `House of ${DASHA_HOUSE_NAMES[(occupiesHouse - 1)] || ''}`;
    const rulesHouses: number[] = level.rules || [];
    const aspectsHouses: number[] = level.aspects || [];

    // Duration string (e.g. "16y" or "2y 3m")
    const fromMs = new Date(level.from || '').getTime();
    const toMs = new Date(level.to || '').getTime();
    let durationStr = '';
    if (!isNaN(fromMs) && !isNaN(toMs) && toMs > fromMs) {
      const totalDays = (toMs - fromMs) / (1000 * 60 * 60 * 24);
      const years = Math.floor(totalDays / 365.25);
      const months = Math.round((totalDays - years * 365.25) / 30.44);
      durationStr = `${years}y${months > 0 ? ' ' + months + 'm' : ''}`;
    }

    // Life focus areas derived from houses
    const allHouses = Array.from(new Set([...rulesHouses, occupiesHouse, ...aspectsHouses]));
    const seen = new Set<string>();
    const lifeFocusAreas: string[] = [];
    allHouses.sort((a, b) => a - b).forEach(h => {
      (DASHA_HOUSE_TO_LIFE[h] || []).forEach(area => {
        if (!seen.has(area)) { seen.add(area); lifeFocusAreas.push(area); }
      });
    });

    // Transit analysis: single-snapshot approximation of aggregateTransitStats
    // Mirrors FeedsList.tsx display: benefic %, malefic %, transit support score
    let transitAnalysis: { beneficPct: number; maleficPct: number; transitSupport: number } | undefined;
    if (transitPositions && transitPositions.length > 0) {
      const allFocusHouses = Array.from(new Set([...rulesHouses, occupiesHouse, ...aspectsHouses]));
      // Determine which transiting planets are benefic / malefic per functional map
      const beneficPlanets = transitPositions.filter(p => {
        const ft = functionalMap?.[p.planet] || 'neutral';
        return ft === 'benefic' || ft === 'yoga_karaka';
      });
      const maleficPlanets = transitPositions.filter(p => {
        const ft = functionalMap?.[p.planet] || 'neutral';
        return ft === 'malefic';
      });
      // Benefic blessing: conjunctions or 5th/9th aspects on focus houses (mirrors aggregateTransitStats)
      let totalBeneficScore = 0;
      let beneficsOnTarget = 0;
      for (const bp of beneficPlanets) {
        const h = bp.house;
        const fifth = ((h - 1 + 4) % 12) + 1;
        const ninth  = ((h - 1 + 8) % 12) + 1;
        if (allFocusHouses.includes(h) || allFocusHouses.includes(fifth) || allFocusHouses.includes(ninth)) {
          const score = blendedPlanetPct?.[bp.planet] !== undefined ? Math.abs(blendedPlanetPct[bp.planet]!) : 100;
          totalBeneficScore += score;
          beneficsOnTarget++;
        }
      }
      const maxBeneficScore = Math.max(beneficPlanets.length, 1) * 100;
      const beneficPct = beneficsOnTarget > 0
        ? Math.min(100, Math.round(totalBeneficScore / maxBeneficScore * 100))
        : 0;
      // Malefic pressure: conjunction + Parashara aspects on focus houses
      let totalMaleficScore = 0;
      let maleficsOnTarget = 0;
      for (const mp of maleficPlanets) {
        const h = mp.house;
        let aspects = [h];
        if (mp.planet === 'Mars') {
          aspects.push(((h - 1 + 3) % 12) + 1, ((h - 1 + 6) % 12) + 1, ((h - 1 + 7) % 12) + 1);
        } else if (mp.planet === 'Saturn') {
          aspects.push(((h - 1 + 2) % 12) + 1, ((h - 1 + 6) % 12) + 1, ((h - 1 + 9) % 12) + 1);
        } else if (mp.planet === 'Rahu' || mp.planet === 'Ketu') {
          aspects.push(((h - 1 + 4) % 12) + 1, ((h - 1 + 6) % 12) + 1, ((h - 1 + 8) % 12) + 1);
        } else {
          aspects.push(((h - 1 + 6) % 12) + 1);
        }
        if (allFocusHouses.some(fh => aspects.includes(fh))) {
          const score = blendedPlanetPct?.[mp.planet] !== undefined ? Math.abs(blendedPlanetPct[mp.planet]!) : 100;
          totalMaleficScore += score;
          maleficsOnTarget++;
        }
      }
      const maxMaleficScore = Math.max(maleficPlanets.length, 1) * 100;
      const maleficPct = maleficsOnTarget > 0
        ? Math.min(100, Math.round(totalMaleficScore / maxMaleficScore * 100))
        : 0;
      // Transit support: count non-Moon planets occupying focus houses
      const transitSupport = transitPositions.filter(
        p => p.planet !== 'Moon' && allFocusHouses.includes(p.house)
      ).length;
      transitAnalysis = { beneficPct, maleficPct, transitSupport };
    }

    return { nakshatra, zodiacSign, spotlight, occupiesHouse, rulesHouses, aspectsHouses, durationStr, lifeFocusAreas, transitAnalysis };
  };

  let dashaData: any;
  if (dashaContext && dashaContext.MD && dashaContext.AD && dashaContext.PD) {
    // Calculate progress percentages
    const calculateProgress = (start: string, end: string, now: string): number => {
      const startDate = new Date(start).getTime();
      const endDate = new Date(end).getTime();
      const nowDate = new Date(now).getTime();
      
      if (nowDate < startDate) return 0;
      if (nowDate > endDate) return 100;
      
      const total = endDate - startDate;
      const elapsed = nowDate - startDate;
      return Math.max(0, Math.min(100, (elapsed / total) * 100));
    };

    const now = new Date().toISOString();
    
    dashaData = {
      current: {
        MD: {
          planet: dashaContext.MD.planet || 'Unknown',
          start: dashaContext.MD.from || '',
          end: dashaContext.MD.to || '',
          percentage: calculateProgress(
            dashaContext.MD.from || '',
            dashaContext.MD.to || '',
            now
          ),
          ...getDashaExtras(dashaContext.MD),
        },
        AD: {
          planet: dashaContext.AD.planet || 'Unknown',
          start: dashaContext.AD.from || '',
          end: dashaContext.AD.to || '',
          percentage: calculateProgress(
            dashaContext.AD.from || '',
            dashaContext.AD.to || '',
            now
          ),
          ...getDashaExtras(dashaContext.AD),
        },
        PD: {
          planet: dashaContext.PD.planet || 'Unknown',
          start: dashaContext.PD.from || '',
          end: dashaContext.PD.to || '',
          percentage: calculateProgress(
            dashaContext.PD.from || '',
            dashaContext.PD.to || '',
            now
          ),
        },
      },
      timeline: [],
    };
  } else {
    dashaData = {
      current: {
        MD: { planet: 'Unknown', start: '', end: '', percentage: 0 },
        AD: { planet: 'Unknown', start: '', end: '', percentage: 0 },
        PD: { planet: 'Unknown', start: '', end: '', percentage: 0 },
      },
      timeline: [],
    };
  }

  // Build report data
  const reportData: ReportData = {
    birthDetails: {
      name: birthDetails?.name || 'Native',
      dateOfBirth: birthDetails?.dateOfBirth || 'Not specified',
      timeOfBirth: birthDetails?.timeOfBirth || 'Not specified',
      location: birthDetails?.location || 'Not specified',
      coordinates: birthDetails?.coordinates || { lat: 0, lon: 0 },
      timezone: birthDetails?.timezone || 'UTC',
      ayanamsa: 'Lahiri',
      ascendant: ascendantSign,
      ascendantLord: ascendantLord,
      moonSign: moonSign,
      moonNakshatra: moonNakshatra,
      ascendantNakshatra,
      ascendantPersonality: birthDetails?.ascendantPersonality,
      moonNakshatraDesc: birthDetails?.moonNakshatraDesc,
      houseCusps,
      houseMetricsTimestamp: _houseMetricsTimestamp,
      planetsInHouses,
      planetaryAspects,
    },
    charts: { d1: chartImageUrl },
    planets: planets as any[],
    houseScores: houseScores,
    lifeAreas: lifeAreas,
    yogas: processedYogas,
    dashas: dashaData,
    feedWindows: feeds && feeds.length > 0 ? feeds.filter(f => f.window?.from && f.window?.to).map(f => ({ ...f, private: f.private || false })) : undefined,
    houseLords: (() => {
      const result: Record<number, { lord: string; lordHouse: number }> = {};
      houseCusps.forEach(({ house, sign }) => {
        const sIdx = signNames.indexOf(sign) + 1; // 1-12
        const lord = signLords[sIdx] || 'Unknown';
        const lordHouse = (astroData.placements as any)?.[lord]?.house ?? 0;
        result[house] = { lord, lordHouse };
      });
      return result;
    })(),
    houseActivities: houseActivities ?? undefined,
    lordDusthanaPenalties: lordDusthanaPenalties ?? undefined,
    planetFactorData: (() => {
      // Build once using the already-constructed planets[] array + shadbala/ik/blended data
      const PLANET_ORDER = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];
      return PLANET_ORDER.map(pName => {
        const p = (planets as any[]).find(pl => pl?.name === pName);
        if (!p) return null;
        const sb = shadbalaPct?.[pName];
        const sr = shadbalaRatio?.[pName];
        const ik = ikPhala?.[pName];
        const bl = blendedPlanetPct?.[pName];
        return {
          name: pName,
          sign: p.sign,
          house: p.house,
          dignity: p.dignity,
          isRetrograde: p.isRetrograde,
          isCombust: p.isCombust,
          shadbalaTotalPct: sb?.totalPct != null ? Math.round(sb.totalPct) : undefined,
          sthanaBala:       sb?.components?.sthana     != null ? Math.round(sb.components.sthana)     : undefined,
          digBala:          sb?.components?.dig         != null ? Math.round(sb.components.dig)         : undefined,
          kalaBala:         sb?.components?.kala        != null ? Math.round(sb.components.kala)        : undefined,
          cheshtaBala:      sb?.components?.cheshta     != null ? Math.round(sb.components.cheshta)     : undefined,
          naisargikaBala:   sb?.components?.naisargika  != null ? Math.round(sb.components.naisargika)  : undefined,
          drikBala:         sb?.components?.drik        != null ? Math.round(sb.components.drik)        : undefined,
          virupas:          sr?.virupa       != null ? Math.round(sr.virupa)       : undefined,
          virupasRequired:  sr?.minRequired  != null ? Math.round(sr.minRequired)  : undefined,
          virupasRatio:     sr?.ratio        != null ? Math.round(sr.ratio * 100) / 100 : undefined,
          ishta:            ik?.ishta        != null ? Math.round(ik.ishta)        : undefined,
          kashta:           ik?.kashta       != null ? Math.round(ik.kashta)       : undefined,
          ishtaPct:         ik?.ishtaPct     != null ? Math.round(ik.ishtaPct)     : undefined,
          kashtaPct:        ik?.kashtaPct    != null ? Math.round(ik.kashtaPct)    : undefined,
          blendedStrength:  bl               != null ? Math.round(bl * 10) / 10   : undefined,
        };
      }).filter(Boolean) as NonNullable<import('./types').ReportData['planetFactorData']>;
    })(),
    generatedAt: new Date().toISOString(),
    reportType: 'comprehensive',

    // Year-ahead forecast — only populated when UTC birth time is available
    yearForecastInput: utcBirthISO
      ? {
          birthISO: utcBirthISO,
          moonLon:  (astroData as any)?.moon?.siderealLongitudeDeg
                 ?? (astroData as any)?.grahas?.Moon?.lon
                 ?? 0,
        }
      : undefined,
  };

  return reportData;
}
